// ============= Imports =============
// External dependencies
import { JSX, useCallback, useEffect, useState } from 'react';
import {
  FormStack,
  SearchBarWithDropdown,
  LoadingSpinner,
  Spacer,
} from '@tw/tw-component-library';

// Internal hooks
import { useGraphData } from '../../hooks/useGraphData';
import { useSharePointData } from '../../hooks/useSharePointData';
import { useAppState } from '../../components/container/forms/hooks/useAppState';
import { useFormState } from '../../components/container/forms/hooks/useFormState';
import { useFormConfiguration } from '../../components/container/forms/hooks/useFormConfiguration';
import { useSearch } from '../../components/container/forms/hooks/useSearch';

// Utils and Types
import { convertToTagOptions } from '../../utils/transformers';
import { HomeProps } from '../../types';
import { Suggestion } from '../../components/container/forms/types';

// Styled Components
import { Container, SuccessMessage, Wrapper, ErrorMessage } from './styles';

// ============= Constants =============
const SUCCESS_MESSAGE_DURATION = 3000;

// ============= Component =============
/**
 * Home Component
 * Main component for user management interface
 *
 * @param {HomeProps} props - Component props
 * @returns {JSX.Element} Rendered component
 */
export const Home = ({ getAccessToken }: HomeProps): JSX.Element => {
  // ============= Hooks Initialization =============
  /**
   * Data fetching hooks
   */
  const { fetchGraphData } = useGraphData(getAccessToken);
  //TODO Fix Type Error
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  //@ts-expect-error
  const { fetchSharePointData, fetchSharePointUser } =
    useSharePointData(getAccessToken);

  /**
   * State management hooks
   */
  const { state, setState, showSuccess, isMounted, resetAppState } =
    useAppState(SUCCESS_MESSAGE_DURATION);

  const {
    formData,
    paUserDetails,
    tagPickerSuggestions,
    pendingRemovals,
    handleTagsChange,
    handleFloorChange,
    setFormData,
    setPaUserDetails,
    setTagPickerSuggestions,
    setPendingRemovals,
    resetFormState,
  } = useFormState();

  /**
   * Search functionality hooks
   */
  const { suggestions, handleSelect, handleSearch, handleTagPickerSearch } =
    useSearch({
      //TODO Fix Type Error
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      //@ts-expect-error
      fetchGraphData,
      setState,
      setTagPickerSuggestions,
    });

  // =========== State ============
  // Local state for dismissing messages
  const [showError, setShowError] = useState(true);

  // ============= Callbacks =============
  /**
   * Handles successful form submission
   */
  const handleSuccess = useCallback(() => {
    if (isMounted.current) {
      resetFormState();
      resetAppState();
      showSuccess();
    }
  }, [resetFormState, resetAppState, showSuccess, isMounted]);

  /**
   * Form configuration setup
   */
  const { formConfig, formHandlers } = useFormConfiguration({
    getAccessToken,
    state,
    setState,
    formData,
    paUserDetails,
    pendingRemovals,
    setPendingRemovals,
    handleTagsChange,
    handleFloorChange,
    setFormData,
    setPaUserDetails,
    handleSuccess,
    fetchSharePointData,
    handleTagPickerSearch,
    tagPickerSuggestions,
    fetchSharePointUser,
  });

  // ============= Effects =============
  /**
   * Effect to clear PA values when paUserDetails is empty
   */
  useEffect(() => {
    if (paUserDetails.length === 0) {
      setFormData((prev) => ({
        ...prev,
        twlegalPA: [],
      }));
      console.log('Cleared PA values in form data');
    }
  }, [paUserDetails, setFormData]);

  /**
   * Effect to fetch user data when email changes
   */
  useEffect(() => {
    if (!state.email) return;

    const fetchUserData = async () => {
      setState((prev) => ({ ...prev, isLoading: true }));
      try {
        const data = await fetchSharePointData(state.email);
        if (data) {
          //TODO Fix Type Error
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          //@ts-expect-error
          const tagsWithKeys = convertToTagOptions(data.paUserDetails);
          const newFloor = data.roomFloorValues?.[0]?.value || '';

          setPaUserDetails(tagsWithKeys);
          setFormData((prev) => ({
            ...prev,
            twlegalPA: tagsWithKeys.map((tag) => tag.value),
            floorRoom: newFloor,
          }));
        }
      } catch (error) {
        setState((prev) => ({
          ...prev,
          error: `Error fetching user data : ${error}`,
        }));
      } finally {
        setState((prev) => ({ ...prev, isLoading: false }));
      }
    };

    fetchUserData();
  }, [
    state.email,
    fetchSharePointData,
    setPaUserDetails,
    setFormData,
    setState,
  ]);

  useEffect(() => {
    if (state.error) setShowError(true);
  }, [state.error]);

  // ============= Render =============
  return (
    <Container>
      <Wrapper>
        <SearchBarWithDropdown
          options={suggestions}
          placeholder="Search for a user"
          onSelect={(option: Suggestion | null) => handleSelect(option)}
          onSearch={handleSearch}
          value={state.selectedUser?.label || ''}
          searchButton={true}
          key={state.selectedUser?.email || 'empty'}
          getDisplayText={(option: Suggestion) => String(option.label)}
          filterOptions={(options: Suggestion[], term: string) => {
            return options.filter(
              (option) =>
                option.label.toLowerCase().includes(term.toLowerCase()) ||
                option.email.toLowerCase().includes(term.toLowerCase())
            );
          }}
        />
        <Spacer size={2} />

        {state.isLoading && <LoadingSpinner />}

        {state.selectedUser && !state.isLoading && !state.error && (
          //@ts-expect-error i will need to fix this
          <FormStack {...formConfig} {...formHandlers} initialData={formData} />
        )}

        {state.error && showError && (
          <ErrorMessage role="alert" aria-live="assertive">
            Error: {state.error}
            <button
              style={{
                position: 'absolute',
                right: 8,
                top: 8,
                background: 'none',
                border: 'none',
                color: '#721c24',
                fontWeight: 'bold',
                cursor: 'pointer',
                fontSize: 18,
                lineHeight: 1,
              }}
              aria-label="Dismiss error"
              onClick={() => setShowError(false)}
            >
              ×
            </button>
          </ErrorMessage>
        )}
        {state.showSuccessMessage && (
          <SuccessMessage role="status" aria-live="polite">
            Changes saved successfully!
            <button
              style={{
                position: 'absolute',
                right: 8,
                top: 8,
                background: 'none',
                border: 'none',
                color: '#155724',
                fontWeight: 'bold',
                cursor: 'pointer',
                fontSize: 18,
                lineHeight: 1,
              }}
              aria-label="Dismiss success"
            >
              ×
            </button>
          </SuccessMessage>
        )}
      </Wrapper>
    </Container>
  );
};
